import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAiRebalanceForecast } from "../../hooks/useAiRebalanceForecast";
import { AiRebalanceForecastPanel } from "./aiRebalanceTableComponents/AiRebalanceForecastPanel";
import { LogStateButtonsTable } from "./aiRebalanceTableComponents/LogStateButtonsTable";
import { DecisionLogsHeader } from "./aiRebalanceTableComponents/DecisionLogsHeader";
import { ListOfLogs } from "./aiRebalanceTableComponents/ListOfLogs";
import { GovernedCounter } from "./aiRebalanceTableComponents/GovernedCounter";
import { DescriptionAiRebalance } from "./aiRebalanceTableComponents/DescriptionAiRebalance";
import { LoaderSpinner } from "../LoaderSpinner";

const AiRebalanceTable = ({
    selectedForecastDemandPlanning,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
    searchTerm,
    onServerRecordUpdated,
}) => {
    const [receivedLogsIsOpened, setReceivedLogsIsOpened] = useState(true);
    const [selectedLogStatus, setSelectedLogStatus] = useState("all");

    const {
        forecastYearStr,
        tableSnapshot,
        revenueTarget,
        closedMonths,
        recommendations,
        decisionLogs,
        appliedDecisions,
        loadingRebalanceActuals,
        loadingHistory,
        saving,
        applyRevenueTarget,
        applyRebalanceCategoryPct,
        applyRebalanceSubCategoryPct,
        applySkuRebalance,
        pendingSkuRebalanceGroups,
        governanceCounts,
        tableRecords,
        recordDecision,
    } = useAiRebalanceForecast({
        selectedForecastDemandPlanning,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
        searchTerm,
        onServerRecordUpdated,
    });

    const { queuedCount, pendingCount, historyCount } = governanceCounts;

    const filteredLogs = useMemo(() => {
        if (selectedLogStatus === "approved") {
            return decisionLogs.filter((entry) => entry.isApproved);
        }
        if (selectedLogStatus === "dismissed") {
            return decisionLogs.filter((entry) => entry.isDissmissed);
        }
        return decisionLogs;
    }, [decisionLogs, selectedLogStatus]);

    const forecastPanelRef = useRef(null);
    const [forecastPanelHeight, setForecastPanelHeight] = useState(null);

    useLayoutEffect(() => {
        const panel = forecastPanelRef.current;
        if (!panel) return undefined;

        const updateHeight = () => {
            setForecastPanelHeight(panel.getBoundingClientRect().height);
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(panel);

        return () => observer.disconnect();
    }, [
        loadingRebalanceActuals,
        pendingSkuRebalanceGroups,
        recommendations,
        tableSnapshot,
    ]);

    if (!selectedForecastDemandPlanning?.id) {
        return (
            <section className="flex-1 px-4 py-4 lg:px-6">
                <div className="rounded-lg border border-dashed border-border bg-white px-4 py-8 text-sm text-gray-600">
                    Select a forecast demand planning record to review AI rebalance recommendations.
                </div>
            </section>
        );
    }

    return (
        <section className="flex-1 py-1">
            <div className="mb-4 rounded-lg border border-border bg-white p-3 shadow-sm">
                <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.85fr)]">
                    <div
                        ref={forecastPanelRef}
                        className="rounded-lg border border-border bg-background/70 p-3"
                    >
                        <div className="flex flex-col gap-3 border-b border-border pb-3 lg:flex-row lg:items-start lg:justify-between">
                            <DescriptionAiRebalance />
                            <GovernedCounter
                                queuedCount={queuedCount}
                                pendingCount={pendingCount}
                                historyCount={historyCount}
                            />
                        </div>
                        {loadingRebalanceActuals ? (
                            <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 py-10 my-10">
                                <LoaderSpinner border="4" width="50" height="50" colorSpinner="black"/>
                                <p className="text-xs text-gray-500">
                                    Refreshing forecast revenue actuals…
                                </p>
                            </div>
                        ) : (
                            <AiRebalanceForecastPanel
                                appliedDecisions={appliedDecisions}
                                applyRebalanceCategoryPct={applyRebalanceCategoryPct}
                                applyRebalanceSubCategoryPct={applyRebalanceSubCategoryPct}
                                applyRevenueTarget={applyRevenueTarget}
                                applySkuRebalance={applySkuRebalance}
                                closedMonths={closedMonths}
                                forecastYearStr={forecastYearStr}
                                pendingSkuRebalanceGroups={pendingSkuRebalanceGroups}
                                recommendations={recommendations}
                                recordDecision={recordDecision}
                                revenueTarget={revenueTarget}
                                saving={saving}
                                tableRecords={tableRecords}
                                tableSnapshot={tableSnapshot}
                            />
                        )}
                    </div>
                    <aside
                        className="flex min-h-0 flex-col rounded-lg border border-border bg-background/70 p-3"
                        style={forecastPanelHeight ? { height: forecastPanelHeight } : undefined}
                    >
                        <DecisionLogsHeader
                            receivedLogsIsOpened={receivedLogsIsOpened}
                            setReceivedLogsIsOpened={setReceivedLogsIsOpened}
                        />
                        {receivedLogsIsOpened && (
                            <>
                                <LogStateButtonsTable
                                    selectedLogStatus={selectedLogStatus}
                                    setSelectedLogStatus={setSelectedLogStatus}
                                />
                                <div className="min-h-0 flex-1 overflow-auto">
                                    {loadingHistory ? (
                                        <LoaderSpinner border="4" width="40" height="40" colorSpinner="black" />
                                    ) : (
                                        <ListOfLogs list={filteredLogs} />
                                    )}
                                </div>
                            </>
                        )}
                    </aside>
                </div>
            </div>
        </section>
    );
};

export default AiRebalanceTable;
